import type { Permission } from '@jaf/contracts';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  CalendarCheck,
  ChartNoAxesColumn,
  Contact,
  FileUp,
  GitCompareArrows,
  Globe,
  Landmark,
  Layers,
  ListTree,
  Network,
  ShieldCheck,
  Truck,
  Users,
  Wallet,
  WalletCards,
  type LucideIcon,
} from 'lucide-react';
import type { CanFunction } from '@/features/access-control';
import { paths } from '@/shared/lib/paths';

/**
 * Item do menu principal: um link (com `to` e a permissão que a tela exige), um grupo (só
 * `children`) ou os dois (link com subitens). `platformAdmin` marca as telas do admin geral
 * da plataforma (ADR-0021), que não dependem de permissão na organização.
 */
export interface NavigationItem {
  label: string;
  icon: LucideIcon;
  to?: string;
  permission?: Permission;
  platformAdmin?: true;
  children?: readonly NavigationItem[];
}

/**
 * Menu principal. Para incluir uma tela, registre aqui com a mesma permissão que a rota exige
 * (`RequirePermission` no router).
 */
export const NAVIGATION: readonly NavigationItem[] = [
  {
    label: 'Administração geral',
    icon: Globe,
    platformAdmin: true,
    children: [
      {
        label: 'Organizações',
        to: paths.platformOrganizations(),
        platformAdmin: true,
        icon: Network,
      },
      {
        label: 'Admins gerais',
        to: paths.platformAdministrators(),
        platformAdmin: true,
        icon: ShieldCheck,
      },
    ],
  },
  { label: 'Empresas', to: paths.companies(), permission: 'companies:read', icon: Building2 },
  { label: 'Clientes', to: paths.parties('customers'), permission: 'parties:read', icon: Contact },
  {
    label: 'Fornecedores',
    to: paths.parties('suppliers'),
    permission: 'parties:read',
    icon: Truck,
  },
  { label: 'Usuários', to: paths.users(), permission: 'users:manage', icon: Users },
  {
    label: 'Financeiro',
    icon: Wallet,
    children: [
      {
        label: 'Contas',
        to: paths.titles(),
        permission: 'titles:read',
        icon: WalletCards,
        children: [
          {
            label: 'Contas a pagar',
            to: paths.titles('PAYABLE'),
            permission: 'titles:read',
            icon: ArrowUpFromLine,
          },
          {
            label: 'Contas a receber',
            to: paths.titles('RECEIVABLE'),
            permission: 'titles:read',
            icon: ArrowDownToLine,
          },
        ],
      },
      {
        label: 'Contas bancárias',
        to: paths.bankAccounts(),
        permission: 'bank-accounts:read',
        icon: Landmark,
      },
      {
        label: 'Importações',
        to: paths.imports(),
        permission: 'titles:create',
        icon: FileUp,
      },
      {
        label: 'Conciliação bancária',
        to: paths.bankReconciliation(),
        permission: 'bank-reconciliation:read',
        icon: GitCompareArrows,
      },
      {
        label: 'Classificações',
        to: paths.classifications(),
        permission: 'ledger-accounts:read',
        icon: ListTree,
      },
      {
        label: 'Centros de custo',
        to: paths.costCenters(),
        permission: 'cost-centers:read',
        icon: Layers,
      },
      {
        label: 'DRE',
        to: paths.incomeStatement(),
        permission: 'income-statement:read',
        icon: ChartNoAxesColumn,
      },
      {
        label: 'Fechamento',
        to: paths.accountingPeriods(),
        permission: 'accounting-periods:read',
        icon: CalendarCheck,
      },
    ],
  },
];

/** Quem vê o menu: as permissões no escopo e se é admin geral da plataforma. */
export interface NavigationViewer {
  platformAdmin: boolean;
}

/** Mantém os links permitidos e os grupos que ainda têm algum item. */
function allowed(
  items: readonly NavigationItem[],
  can: CanFunction,
  viewer: NavigationViewer,
): NavigationItem[] {
  return items.flatMap((item) => {
    if (item.permission !== undefined && !can(item.permission)) return [];
    if (item.platformAdmin === true && !viewer.platformAdmin) return [];
    const children = allowed(item.children ?? [], can, viewer);
    if (item.to === undefined && children.length === 0) return [];
    return [{ ...item, ...(children.length === 0 ? { children: undefined } : { children }) }];
  });
}

/** Itens do menu permitidos no escopo selecionado (controle visual; a API revalida). */
export function navigationFor(
  can: CanFunction,
  viewer: NavigationViewer = { platformAdmin: false },
): NavigationItem[] {
  return allowed(NAVIGATION, can, viewer);
}
